// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { buildAccountRoleOptions } from '../../../../core/utils/accountRoleOptions.ts'
import {
  mapColumnToInputManagerField,
  mapValidationErrors,
  handleAutoPopulation,
  reportingModuleKeyToTableInputManagerModule,
  type TableInputManagerModule
} from '../../../shared/table_input_manager_mappers/index.ts'
import { updateCompanyRecord } from '../../../../state_manager/state_update_actions/companies/index.ts'
import { updateContactRecord } from '../../../../state_manager/state_update_actions/contacts/index.ts'
import { FieldTypeEnum } from '../../../../core/components/InputManager/types.ts'
import { AccountRecordFields, type AccountRecord } from '../../../../state_manager/data_models/account_record.ts'
import { RoundEntryFields } from '../../../../state_manager/data_models/round_entry.ts'
import { AccountRoleEntryFields } from '../../../../state_manager/data_models/account_role_entry.ts'
import { createTableColumns } from '../../../../ui_configs/table_grid_columns.ts'
import { VIEW_TASK_CONFIG } from '../../../../ui_configs/fund_management/view_task_config.ts'

import TableView from '../../../../core/components/TableView/TableView.tsx'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import type { FilterConfig } from '../../../../core/components/TableView/TableViewTypes.ts'

interface CompaniesTableProps {
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  currentPage: number
  cellEditErrors: Record<string, string>
  activeFilters: any[]
  activeSorting: any
  filterOptions: any
  filterOptionsLoading: boolean
  onLoadMore: (page: number, pageSize: number) => void
  onFiltersChange: (filters: FilterConfig) => void
  onSortChange: (sortConfig: any) => void
  /** InputManager's link `onClick`: the column key, the cell value and the record id (its `record` argument is `recordId`). */
  onCompanyNameClick: (key: string, value: any, companyId: string | number) => void
  onTasksClick?: (allTasks: any[], record: any, taskId: string | null) => void
  onTaskToggle?: (taskId: string, recordId: string, completed: boolean) => void
  getLookupLabel: (lookupId: string) => string
  getLookupOptions: (category: string) => any[]
  formatAmountById: (amount: number | null, currencyId: string | null) => string
  /** Column definitions from global configs (`reporting_column_defaults` + per-report `reporting_columns`) + user prefs. */
  gridColumns: ViewConfigColumn[]
}

/**
 * Companies table component with virtualized scrolling and editing capabilities
 */
const CompaniesTable: React.FC<CompaniesTableProps> = ({
  loading,
  loadingMore,
  hasMore,
  currentPage,
  cellEditErrors,
  activeFilters,
  activeSorting,
  filterOptions,
  filterOptionsLoading,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onCompanyNameClick,
  onTasksClick,
  onTaskToggle,
  getLookupLabel,
  getLookupOptions,
  formatAmountById,
  gridColumns
}) => {
  const dispatch = useDispatch()

  // Get countries data from Global Configs Redux state
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  // Get account_role_choices and funds from Global Configs Redux state
  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const fundsData = useSelector((state: any) => state.globalConfigs?.funds || [])
  const shareRegisters = useSelector((state: any) => state.globalConfigs?.share_registers || [])

  // Get updated companies data from Redux state
  const companiesData = useSelector((state: any) => state.companies?.data || [])

  // Currency functionality for number formatting
  const { getCurrencyById } = useCurrencies()

  // Helper function to get role label by UUID
  const getRoleLabel = useCallback(
    (roleId: string): string => {
      const roleChoice = accountRoleChoices.find((choice: any) => choice.id === roleId)
      return roleChoice?.role || ''
    },
    [accountRoleChoices]
  )

  // Helper function to format account_role_choices as options array
  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getShareRegisterOptions = useCallback(() => {
    return shareRegisters.map((register: any) => ({
      value: register.id,
      label: register.label
    }))
  }, [shareRegisters])

  const getFundOptions = useCallback(() => {
    return fundsData.map((fund: any) => ({
      value: fund.id,
      label: fund.name || ''
    }))
  }, [fundsData])

  // Handle successful PATCH requests
  const handlePatchSuccess = useCallback(
    (recordId: string, updates: Record<string, any>) => {
      // Use the country code to get and set the country label
      // Check if the key exists in updates (not just if it's truthy) to handle null values when clearing
      if (AccountRecordFields.accounts__business_country_code in updates) {
        const countryCode = updates[AccountRecordFields.accounts__business_country_code]
        if (countryCode) {
          updates[AccountRecordFields.accounts__business_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          // Country code is null/empty - clear the label
          updates[AccountRecordFields.accounts__business_country_label] = null
        }
      }

      // Handle account_roles updates - process actions and update Redux state
      if (
        updates[AccountRecordFields.prog__account_roles] &&
        Array.isArray(updates[AccountRecordFields.prog__account_roles])
      ) {
        const company = companiesData?.find((c: AccountRecord) => c[AccountRecordFields.accounts__id] === recordId)

        if (company) {
          const currentRoles = company[AccountRecordFields.prog__account_roles] || []
          const rolesWithActions = updates[AccountRecordFields.prog__account_roles]

          // Process actions to build updated roles array
          const updatedRoles: any[] = []
          const rolesToDelete = new Set<string>()

          // First, collect IDs to delete
          rolesWithActions.forEach((roleEntry: any) => {
            if (roleEntry[AccountRoleEntryFields.prog__account_roles__action] === 'delete') {
              const roleId = roleEntry[AccountRoleEntryFields.account_roles__id]
              if (roleId) {
                rolesToDelete.add(roleId)
              }
            }
          })

          // Keep existing roles that aren't being deleted
          currentRoles.forEach((role: any) => {
            const roleId = role?.[AccountRoleEntryFields.account_roles__id]
            if (roleId && !rolesToDelete.has(roleId)) {
              updatedRoles.push(role)
            }
          })

          // Add/update roles from actions
          rolesWithActions.forEach((roleEntry: any) => {
            const action = roleEntry[AccountRoleEntryFields.prog__account_roles__action]
            const roleChoiceId = roleEntry[AccountRoleEntryFields.account_roles__account_role_choice_id]
            const roleId = roleEntry[AccountRoleEntryFields.account_roles__id]

            if (action === 'insert' || action === 'update') {
              // Find if this role already exists in updatedRoles
              const existingIndex = updatedRoles.findIndex(
                (r: any) => r[AccountRoleEntryFields.account_roles__account_role_choice_id] === roleChoiceId
              )

              const roleObject: any = {
                [AccountRoleEntryFields.account_roles__account_role_choice_id]: roleChoiceId
              }

              if (roleId) {
                roleObject[AccountRoleEntryFields.account_roles__id] = roleId
              }

              if (existingIndex >= 0) {
                // Update existing role
                updatedRoles[existingIndex] = { ...updatedRoles[existingIndex], ...roleObject }
              } else {
                // Add new role
                updatedRoles.push(roleObject)
              }
            }
          })

          // Update the roles in the updates object
          updates[AccountRecordFields.prog__account_roles] = updatedRoles
        }
      }

      // Handle round field updates - need to update nested prog__recent_round object
      const company = companiesData?.find((c: AccountRecord) => c[AccountRecordFields.accounts__id] === recordId)

      if (company) {
        const hasRoundFields =
          updates[RoundEntryFields.rounds__pre_money_valuation] !== undefined ||
          updates[RoundEntryFields.rounds__post_money_valuation] !== undefined ||
          updates.preMoneyValue !== undefined ||
          updates.postMoneyValue !== undefined ||
          updates[RoundEntryFields.rounds__name] !== undefined ||
          updates[RoundEntryFields.rounds__funding_stage] !== undefined ||
          updates[RoundEntryFields.rounds__round_close_date] !== undefined ||
          updates.name !== undefined ||
          updates.funding_stage !== undefined

        if (hasRoundFields) {
          // Determine which round to update based on the field being updated
          // valuation_round uses prog__recent_round, latest_round and funding_stage use prog__latest_round
          const isValuationRoundField =
            updates[RoundEntryFields.rounds__name] !== undefined &&
            updates.name !== undefined &&
            company[AccountRecordFields.prog__recent_round]?.[RoundEntryFields.rounds__name] !== undefined

          const currentRound = isValuationRoundField
            ? company[AccountRecordFields.prog__recent_round]
            : company[AccountRecordFields.prog__latest_round] || company[AccountRecordFields.prog__recent_round]

          const roundId = updates.id || currentRound?.[RoundEntryFields.rounds__id] || null

          if (roundId && currentRound) {
            // Update the nested round object
            const updatedRound = {
              ...currentRound
            }

            // Map source keys (preMoneyValue/postMoneyValue) to Redux field names
            if (updates.preMoneyValue !== undefined) {
              updatedRound[RoundEntryFields.rounds__pre_money_valuation] = updates.preMoneyValue
            }
            if (updates.postMoneyValue !== undefined) {
              updatedRound[RoundEntryFields.rounds__post_money_valuation] = updates.postMoneyValue
            }

            // Use Redux field names directly if present (they take precedence)
            if (updates[RoundEntryFields.rounds__pre_money_valuation] !== undefined) {
              updatedRound[RoundEntryFields.rounds__pre_money_valuation] =
                updates[RoundEntryFields.rounds__pre_money_valuation]
            }
            if (updates[RoundEntryFields.rounds__post_money_valuation] !== undefined) {
              updatedRound[RoundEntryFields.rounds__post_money_valuation] =
                updates[RoundEntryFields.rounds__post_money_valuation]
            }

            // Handle round name (from name field or rounds__name)
            if (updates.name !== undefined) {
              updatedRound[RoundEntryFields.rounds__name] = updates.name
            }
            if (updates[RoundEntryFields.rounds__name] !== undefined) {
              updatedRound[RoundEntryFields.rounds__name] = updates[RoundEntryFields.rounds__name]
            }

            // Handle funding stage (from funding_stage field or rounds__funding_stage)
            if (updates.funding_stage !== undefined) {
              updatedRound[RoundEntryFields.rounds__funding_stage] = updates.funding_stage
            }
            if (updates[RoundEntryFields.rounds__funding_stage] !== undefined) {
              updatedRound[RoundEntryFields.rounds__funding_stage] = updates[RoundEntryFields.rounds__funding_stage]
            }

            // Handle round close date
            if (updates[RoundEntryFields.rounds__round_close_date] !== undefined) {
              updatedRound[RoundEntryFields.rounds__round_close_date] =
                updates[RoundEntryFields.rounds__round_close_date]
            }

            // Update the appropriate round object in the company record
            if (isValuationRoundField) {
              updates[AccountRecordFields.prog__recent_round] = updatedRound
            } else {
              // Update both latest_round and recent_round if they're the same round
              const currentRecentRound = company[AccountRecordFields.prog__recent_round]
              if (currentRecentRound && currentRecentRound[RoundEntryFields.rounds__id] === roundId) {
                updates[AccountRecordFields.prog__recent_round] = updatedRound
              }
              updates[AccountRecordFields.prog__latest_round] = updatedRound
            }

            // Remove source keys from updates to avoid setting them on the top-level record
            delete updates.preMoneyValue
            delete updates.postMoneyValue
            delete updates.name
            delete updates.funding_stage
            delete updates.id
          }
        }
      }

      dispatch(
        updateCompanyRecord({
          companyId: recordId,
          updates
        })
      )
    },
    [dispatch, countriesData, companiesData]
  )

  // InputManager render function for cell editing
  const renderInputManagerColumn = useCallback(
    (
      record: AccountRecord,
      columnKey: string,
      fieldId: string,
      fieldType: FieldTypeEnum,
      editingContext: any,
      columnInfo: any = {},
      action?: any
    ) => {
      const isRequired = columnInfo.required || false
      const columnTitle = columnInfo.title || columnKey
      const isColumnEditable = columnInfo.editable !== false

      // Get updated record from Redux state instead of using static record
      const updatedRecord =
        companiesData?.find(
          (company: AccountRecord) =>
            company[AccountRecordFields.accounts__id] === record[AccountRecordFields.accounts__id]
        ) || record

      const reportingModuleKey = columnInfo.moduleKey as string | undefined
      const inputManagerModule: TableInputManagerModule = reportingModuleKeyToTableInputManagerModule(
        reportingModuleKey,
        'companies'
      )

      const companiesHelpers = {
        getLookupLabel,
        formatAmountById,
        getCurrencyById,
        getLookupOptions,
        getRoleLabel,
        getRoleOptions,
        getFundOptions,
        getShareRegisterOptions
      }
      const contactsHelpers = {
        getLookupLabel,
        getLookupOptions
      }

      const helpers =
        inputManagerModule === 'contacts'
          ? contactsHelpers
          : companiesHelpers

      const mappedConfig = mapColumnToInputManagerField(
        inputManagerModule,
        {
          dataIndex: columnKey,
          title: columnTitle,
          required: isRequired,
          editable: isColumnEditable,
          tagDisplay: columnInfo.tagDisplay || false
        },
        updatedRecord,
        helpers,
        fieldType
      )

      const fieldConfig = {
        ...mappedConfig,
        key: `${record[AccountRecordFields.accounts__id]}-${mappedConfig.key}`
      }

      const validationRecordId =
        inputManagerModule === 'contacts'
          ? String((record as { contacts__id?: string }).contacts__id || '')
          : record[AccountRecordFields.accounts__id] || ''

      // Map validation errors
      const validationErrors = mapValidationErrors(inputManagerModule, cellEditErrors, validationRecordId)

      // Handle field value changes
      const handleFieldChange = (fieldKey: string, newValue: any) => {
        // Handle auto-population for related fields
        handleAutoPopulation(
          inputManagerModule,
          fieldKey,
          newValue,
          record,
          (key: string, value: any) => {
            if (editingContext.onCellDataChange) {
              editingContext.onCellDataChange(value)
            }
          },
          helpers
        )

        // Update the cell data
        if (editingContext.onCellDataChange) {
          editingContext.onCellDataChange(newValue)
        }
      }

      // Handle field edit state changes for column width expansion
      const handleFieldEdit = (isEditing: boolean) => {
        if (editingContext?.onFieldEdit) {
          editingContext.onFieldEdit(columnKey, isEditing)
        }
      }

      // Handle field error
      const handleFieldError = (fieldKey: string, error: any) => {
        // Error handling - could be improved with proper error notification
        if (process.env.NODE_ENV === 'development') {
          console.error(`Error in field ${fieldKey}:`, error)
        }
      }

      // Alignment
      const getFieldAlignment = () => {
        if (
          fieldType === FieldTypeEnum.CURRENCY ||
          fieldType === FieldTypeEnum.NUMBER ||
          fieldType === FieldTypeEnum.VALUATION
        ) {
          return 'right'
        }
        return 'left'
      }

      // Apply truncation to all columns except tag displays
      const needsTruncation = !columnInfo.tagDisplay

      // Determine patchId - for round fields, use round ID if available
      const recordIdValue = record[fieldId]
      // Record ids are uuid strings; a numeric id (legacy rows) is stringified the way the PATCH URL would anyway.
      let patchId: string | undefined =
        typeof recordIdValue === 'string'
          ? recordIdValue
          : typeof recordIdValue === 'number'
            ? String(recordIdValue)
            : undefined
      const isRoundField =
        columnKey === 'valuation_round' ||
        columnKey === 'valuation' ||
        columnKey === 'latest_round' ||
        columnKey === 'funding_stage'

      if (columnKey === 'valuation_round' || columnKey === 'valuation') {
        const recentRound = updatedRecord[AccountRecordFields.prog__recent_round]
        patchId = recentRound?.[RoundEntryFields.rounds__id] || patchId
      } else if (columnKey === 'latest_round' || columnKey === 'funding_stage') {
        const latestRound = updatedRecord[AccountRecordFields.prog__latest_round]
        patchId = latestRound?.[RoundEntryFields.rounds__id] || patchId
      }

      // Override editable property for Round fields when roundId (patchId) is missing
      if (isRoundField && fieldConfig) {
        fieldConfig.editable = fieldConfig.editable && !!patchId
      }

      // Always return InputManager - it handles edit/display mode internally
      return (
        <div
          style={{
            width: '100%',
            ...(needsTruncation
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              : {})
          }}
        >
          <InputManager
            inputMode='read'
            labelPosition='none'
            alignment={getFieldAlignment()}
            fields={[fieldConfig]}
            onChange={handleFieldChange}
            onSave={() => {}} // Auto-Patch managed
            onError={handleFieldError}
            onPatchSuccess={(passedCompanyId, updates) => {
              if (inputManagerModule === 'contacts') {
                const contactId = (record as Record<string, string | undefined>).contacts__id
                if (contactId) {
                  dispatch(updateContactRecord({ contactId: String(contactId), updates: { ...updates } }))
                }
              }
              handlePatchSuccess(String(passedCompanyId), updates)
            }}
            onFieldEdit={handleFieldEdit}
            enableAutoPatch={true}
            patchId={patchId}
            recordId={String(record[AccountRecordFields.accounts__id] ?? '')}
            validationErrors={validationErrors}
            className='cell-edit-input-manager'
            onClick={action}
            textClassName={needsTruncation ? 'truncate' : undefined}
            currentRecord={updatedRecord}
          />
        </div>
      )
    },
    [
      cellEditErrors,
      dispatch,
      getLookupLabel,
      formatAmountById,
      getCurrencyById,
      getLookupOptions,
      handlePatchSuccess,
      companiesData,
      getRoleLabel,
      getRoleOptions,
      getFundOptions,
      getShareRegisterOptions
    ]
  )

  // Create refs for callbacks to prevent unnecessary rerenders
  const handlePrimaryActionRef = useRef(onCompanyNameClick)
  const handleSecondaryActionRef = useRef(() => {})
  const handleTasksClickRef = useRef(onTasksClick)
  const handleTaskToggleRef = useRef(onTaskToggle)
  const renderInputManagerColumnRef = useRef(renderInputManagerColumn)

  // Update refs whenever callbacks change
  useEffect(() => {
    handlePrimaryActionRef.current = onCompanyNameClick
  }, [onCompanyNameClick])

  useEffect(() => {
    handleTasksClickRef.current = onTasksClick
  }, [onTasksClick])

  useEffect(() => {
    handleTaskToggleRef.current = onTaskToggle
  }, [onTaskToggle])

  useEffect(() => {
    renderInputManagerColumnRef.current = renderInputManagerColumn
  }, [renderInputManagerColumn])

  const { columns, frozenColumns } = useMemo(
    () =>
      createTableColumns({
        columns: gridColumns,
        fallbackRecordModuleKey: 'accounts',
        handlePrimaryActionRef,
        handleSecondaryActionRef,
        handleTasksClickRef,
        handleTaskToggleRef,
        renderInputManagerColumnRef,
        viewTaskConfig: VIEW_TASK_CONFIG.companies
      }),
    [gridColumns]
  )

  // Deduplicate companies by accounts__id and flatten nested fields for table display
  const dataSource = useMemo(() => {
    if (!companiesData || companiesData.length === 0) return []

    const seenIds = new Set()
    return companiesData
      .filter((company: AccountRecord) => {
        const companyId = company[AccountRecordFields.accounts__id]
        if (!companyId || seenIds.has(companyId)) {
          return false
        }
        seenIds.add(companyId)
        return true
      })
      .map((company: AccountRecord) => {
        const recentRound = company[AccountRecordFields.prog__recent_round]
        const latestRound = company[AccountRecordFields.prog__latest_round]
        const roles = company[AccountRecordFields.prog__account_roles] || []

        // Extract role IDs for the 'role' field
        const roleIds = Array.isArray(roles)
          ? roles
              .map((role: any) => {
                if (typeof role === 'string') return role
                if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
                  return role.account_roles__account_role_choice_id
                }
                return null
              })
              .filter((id: any): id is string => id !== null)
          : []

        return {
          ...company,
          key: company[AccountRecordFields.accounts__id],
          id: company[AccountRecordFields.accounts__id],
          // Flatten nested fields for table columns that use dataIndex
          // Valuation comes from prog__recent_round (requires both date and valuation)
          valuation: recentRound?.[RoundEntryFields.prog__valuation] || null,
          // Valuation Round name comes from prog__recent_round (the round with valuation)
          valuation_round: recentRound?.[RoundEntryFields.rounds__name] || '',
          // Latest round name and funding stage come from prog__latest_round (most recent with date)
          latest_round: latestRound?.[RoundEntryFields.rounds__name] || '',
          funding_stage: latestRound?.[RoundEntryFields.rounds__funding_stage] || '',
          role: roleIds,
          // Keep currency accessible for valuation display (use recent round currency if available)
          currency:
            recentRound?.[RoundEntryFields.rounds__currency] ||
            latestRound?.[RoundEntryFields.rounds__currency] ||
            company.currency ||
            null
        }
      })
  }, [companiesData])

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        onLoadMore={onLoadMore}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        enableCellEditing={true}
        currentFilters={(() => {
          return Array.isArray(activeFilters)
            ? activeFilters.reduce((acc: any, filter: any) => {
                acc[filter.key] = filter.value
                return acc
              }, {})
            : activeFilters || {}
        })()}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={
          Array.isArray(activeSorting) && activeSorting.length > 0
            ? { column: activeSorting[0].key, direction: activeSorting[0].direction }
            : null
        }
        frozenColumns={frozenColumns}
      />
    </div>
  )
}

export default CompaniesTable
